import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import {
  PoMenuModule,
  PoMenuItem,
  PoToolbarModule,
  PoToolbarProfile,
  PoToolbarAction,
  PoAvatarModule
} from '@po-ui/ng-components';

import { LoginService } from './core/services/login.service';
import { ThemeService } from './core/services/theme.service';
import { FilialService } from './core/services/filial.service';
import { IdleTimeoutService } from './core/services/idle-timeout.service';

const AVATAR_URL = 'assets/brand/icon-ascensus.webp';
const APP_TITLE  = 'GNF - Gestão de Notas Fiscais';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    PoToolbarModule,
    PoMenuModule,
    PoAvatarModule
  ],
  templateUrl: './app.html',
  styleUrls: ['./app.css'],
})
export class App {

  isLoginRoute = false;

  profile: PoToolbarProfile = {
    avatar: AVATAR_URL,
    title: 'Usuario',
    subtitle: ''
  };

  profileActions: Array<PoToolbarAction> = [];

  menuLogo$: Observable<string>;

  readonly menus: Array<PoMenuItem> = [
    { label: 'Notas Pendentes',     link: '/notas-list',          icon: 'an an-file-text',       shortLabel: 'Pendentes' },
    { label: 'Notas Entregues',      link: '/notas-entregues',      icon: 'an an-check-circle',    shortLabel: 'Entregue' },
    { label: 'Download PDF',        link: '/download-pdf',        icon: 'an an-download-simple', shortLabel: 'PDF' },
    { label: 'Fornecedores Ocultos', link: '/fornecedores-ocultos', icon: 'an an-eye-slash',       shortLabel: 'Oculto' },
    { label: 'Regras de Classificação', link: '/regras',              icon: 'an an-gear-six',        shortLabel: 'Regras' }
  ];

  constructor(
    private loginService: LoginService,
    private themeService: ThemeService,
    private filialService: FilialService,
    private idleTimeout: IdleTimeoutService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title
  ) {
    this.menuLogo$ = this.themeService.logo$;

    // Auto-logout por inatividade (15 min). Watcher global; so encerra
    // sessao quando ha login ativo (ver IdleTimeoutService).
    this.idleTimeout.start();

    this.themeService.isDark$.subscribe(() => this.rebuildProfileActions());

    this.router.events
      .pipe(filter(ev => ev instanceof NavigationEnd))
      .subscribe((ev: any) => {
        this.isLoginRoute = ev.urlAfterRedirects?.startsWith('/login') ?? false;
        if (!this.isLoginRoute) {
          this.refreshProfile();
          // Pre-carrega lista de filiais (sem UI no toolbar). A inclusao
          // manual de notas (notas-adicionar) consome o cache via
          // FilialService.filiais$ + loadAll() idempotente.
          this.filialService.loadAll().subscribe({ error: () => {} });
        }
        this.updateBrowserTitle();
      });
  }

  private updateBrowserTitle(): void {
    const pageTitle = this.readRouteTitle(this.activatedRoute);
    this.titleService.setTitle(pageTitle ? `${APP_TITLE} - ${pageTitle}` : APP_TITLE);
  }

  private readRouteTitle(route: ActivatedRoute): string | null {
    let current: ActivatedRoute | null = route;
    while (current?.firstChild) {
      current = current.firstChild;
    }
    const t = current?.snapshot.data?.['title'];
    return typeof t === 'string' && t.length > 0 ? t : null;
  }

  private rebuildProfileActions(): void {
    const dark = this.themeService.isDark();
    this.profileActions = [
      {
        label: 'Trocar senha',
        icon: 'an an-key',
        action: () => this.goToChangePassword()
      },
      {
        label: dark ? 'Tema claro' : 'Tema escuro',
        icon: dark ? 'an an-sun' : 'an an-moon',
        action: () => this.toggleTheme()
      },
      {
        label: 'Sair',
        icon: 'po-icon-exit',
        type: 'danger',
        action: () => this.logout()
      }
    ];
  }

  private toggleTheme(): void {
    this.themeService.toggle();
    this.rebuildProfileActions();
  }

  private refreshProfile(): void {
    const name = this.loginService.getUsername() ?? 'Usuario';
    const email = this.loginService.getUserEmail() ?? '';
    this.profile = {
      avatar: AVATAR_URL,
      title: name,
      subtitle: email
    };
  }

  private goToChangePassword(): void {
    this.router.navigate(['/trocar-senha']);
  }

  private logout(): void {
    this.loginService.logout();
  }
}
